// FASE J · Reusable helper that forces a full page reload to a target
// path on mount. Used to redirect legacy SPA landing URLs
// (`/en-US`, `/en-US/*`, `/v2`, `/v3`, `/landing-v1`) to the authoritative
// Next.js SSR landing (served at `/` through the ingress proxy).
//
// Why `window.location.replace()` instead of `<Navigate>` or `useNavigate`:
// React Router stays inside the SPA tree, which would re-render the SPA
// root. The authoritative landing lives on Next.js SSR → the browser
// must re-request the URL from the server, hit the proxy, and let
// Next.js render. `replace()` keeps the back button clean (no history
// entry for the deprecated URL).
//
// 2026-11-01 · prod apex loop fix (auth-aware landing):
// When Next.js SSR is NOT exposed on the ingress (as on
// `globalleadsuite.io` production apex), hitting `/` lands on the CRA
// SPA bundle · `ForceReload to="/"` would call
// `window.location.replace("/")` which reloads the same bundle · infinite
// loop. Guard: if we're already on the target path, redirect based on
// auth state read from `localStorage.getItem("glms_session")` (the same
// non-sensitive flag set by AuthContext · see
// `/app/frontend/src/context/AuthContext.jsx`):
//   - authenticated → `/app/dashboard`
//   - anonymous     → `/pricing`
// Reading localStorage directly (not useContext) because ForceReload may
// mount outside the <AuthProvider> tree depending on route composition.
import React, { useEffect } from "react";

function _isAuthenticated() {
    if (typeof window === "undefined") return false;
    try {
        return window.localStorage.getItem("glms_session") === "1";
    } catch {
        // localStorage can throw on strict privacy / sandboxed iframe.
        return false;
    }
}

export default function ForceReload({ to = "/" }) {
    useEffect(() => {
        if (typeof window === "undefined") return;
        const current = window.location.pathname;
        // Normalise trailing slash so `/it` and `/it/` both match `/it`.
        const norm = (p) => (p !== "/" && p.endsWith("/") ? p.slice(0, -1) : p);

        // 2026-11-01 · locale-prefix detection:
        // React Router v7 does NOT reliably match `/{lc}` route to URL
        // `/{lc}/` (strict trailing slash). When the user lands on
        // `/{lc}/` the catch-all `*` route can render NotFound · but if
        // RR does match (version-dependent), ForceReload mounts with
        // to="/" and `replace("/")` runs. Either way, treat any 2-3
        // char locale prefix (optionally with region, e.g. zh-CN) as
        // collision-equivalent to `/` and short-circuit directly to the
        // auth-aware target. Saves 1 hop + survives RR v7 trailing-
        // slash variations.
        const localeRe = /^\/([A-Za-z]{2,3}(?:-[A-Za-z]{2,4})?)\/?$/;
        const isLocaleRoot = localeRe.test(current);

        if (norm(current) === norm(to) || isLocaleRoot) {
            // Loop guard · we're already here (or on a locale-root
            // equivalent). Route to a safe SPA destination based on
            // auth state.
            if (norm(to) === "" || norm(to) === "/" || isLocaleRoot) {
                const target = _isAuthenticated()
                    ? "/app/dashboard"
                    : "/pricing?from=landing_fallback";
                window.location.replace(target);
            }
            return;
        }
        window.location.replace(to);
    }, [to]);

    return (
        <div
            data-testid="force-reload-shell"
            className="min-h-screen flex items-center justify-center"
            style={{ background: "var(--color-bg-iron, #0B0B0C)" }}
        >
            <div
                className="text-sm"
                style={{ color: "var(--color-text-muted, #71717A)" }}
            >
                Redirecting…
            </div>
        </div>
    );
}
